import { lazy, type ReactNode } from "react";
import type { RouteObject } from "react-router-dom";
import UnderDevelopment from "../../features/shared/pages/UnderDevelopment";
import { APP_ROUTES } from "../routePaths";
import { createProtectedIndexRoute, withSuspense } from "./helpers";

const PurchaseOrder = lazy(() => import("../../features/purchaseOrder"));
const PurchaseOrderViewPage = lazy(() => import("../../features/purchaseOrder/PurchaseOrderViewPage"));
const BillsFormPage = lazy(() => import("../../features/bills/pages/BillsFormPage"));
const PaymentPage = lazy(() => import("../../features/payment/pages/PaymentPage.tsx"));
const PaymentBillsViewPage = lazy(() => import("../../features/payment/pages/PaymentBillsViewPage.tsx"));
const PaymentVoucherFormPage = lazy(() => import("../../features/payment-voucher"));
const PaymentVoucherViewPage = lazy(() => import("../../features/payment-voucher/pages/PaymentVoucherViewPage"));
const PaymentCompletedPage = lazy(() => import("../../features/payment-completed"));
const PettyCashFormPage = lazy(() => import("../../features/petty-cash"));
const PettyCashViewPage = lazy(() => import("../../features/petty-cash/pages/PettyCashViewPage"));
const ProfilePage = lazy(() => import("../../features/profile/ProfilePage"));
const SpareEntry = lazy(() => import("../../features/spare-entry"));
const FuelEntryListPage = lazy(() => import("../../features/fuel-entry/pages/FuelEntryListPage"));
const FuelEntryFormPage = lazy(() => import("../../features/fuel-entry/pages/FuelEntryFormPage"));
const TollEntryListPage = lazy(() => import("../../features/toll-entry/pages/TollEntryListPage"));
const TollEntryFormPage = lazy(() => import("../../features/toll-entry/pages/TollEntryFormPage"));
const LeviEntryListPage = lazy(() => import("../../features/levi-entry/pages/LeviEntryListPage"));
const LeviEntryFormPage = lazy(() => import("../../features/levi-entry/pages/LeviEntryFormPage"));
const AutoPassEntryListPage = lazy(() => import("../../features/auto-pass-entry/pages/AutoPassEntryListPage"));
const AutoPassEntryFormPage = lazy(() => import("../../features/auto-pass-entry/pages/AutoPassEntryFormPage"));
const EngineHoursPage = lazy(() => import("../../features/gps/pages/EngineHoursPage"));
const SpeedReportPage = lazy(() => import("../../features/gps/pages/SpeedReportPage"));
const FuelFillingsPage = lazy(() => import("../../features/gps/pages/FuelFillingsPage"));
const SpareEntryViewPage = lazy(() => import("../../features/spare-entry/SpareEntryViewPage"));
const AiSettingsPage = lazy(() => import("../../features/ai-settings"));
const ScreenAccessPage = lazy(() => import("../../features/screen-access"));
const MailMonitorPage = lazy(() => import("../../features/mail-monitor/pages/MailMonitorPage"));
const MailMonitorSettingsPage = lazy(() => import("../../features/mail-monitor/pages/MailMonitorSettingsPage"));
const MailResponseReportPage = lazy(() => import("../../features/mail-monitor/report/pages/MailResponseReportPage"));
const MailAfterHoursPage = lazy(() => import("../../features/mail-monitor/report/pages/MailAfterHoursPage"));
const IrListPage = lazy(() => import("../../features/ir-report/pages/IrListPage"));
const IrFormPage = lazy(() => import("../../features/ir-report/pages/IrFormPage"));

interface RouteConfig {
  path: string;
  element: ReactNode;
}

const utilityRouteConfigs: RouteConfig[] = [
  { path: "/Mainsetting", element: <UnderDevelopment /> },
  { path: "/MyProfile", element: <ProfilePage /> },
  { path: APP_ROUTES.aiSettings, element: withSuspense(<AiSettingsPage />) },
  { path: APP_ROUTES.screenAccess, element: withSuspense(<ScreenAccessPage />) },
  { path: APP_ROUTES.mailMonitor, element: withSuspense(<MailMonitorPage />) },
  { path: APP_ROUTES.mailMonitorSettings, element: withSuspense(<MailMonitorSettingsPage />) },
  { path: APP_ROUTES.mailResponseReport, element: withSuspense(<MailResponseReportPage />) },
  { path: APP_ROUTES.mailAfterHours, element: withSuspense(<MailAfterHoursPage />) },
];

const payableRouteConfigs: RouteConfig[] = [
  { path: "/PurchaseMaster", element: withSuspense(<SpareEntry />) },
  { path: "/PurchaseMaster/View", element: withSuspense(<SpareEntryViewPage />) },
  { path: "/BillsOrderMaster", element: withSuspense(<PurchaseOrder />) },
  // Same screen, opened with one sale order to raise the PO against. The id
  // rides in the path so nothing is left in shared storage for another tab.
  { path: APP_ROUTES.purchaseOrderPush(), element: withSuspense(<PurchaseOrder />) },
  { path: "/BillsOrderMaster/BillsOrderView", element: withSuspense(<PurchaseOrderViewPage />) },
  { path: "/Payment", element: withSuspense(<PaymentPage />) },
  { path: "/Payment/BillsView", element: withSuspense(<PaymentBillsViewPage />) },
  { path: "/ClaimVoucher", element: <UnderDevelopment /> },
  // Payable quick-access target whose screen is not migrated yet. Registered
  // so the dashboard tile lands on the placeholder instead of a 404; the tile
  // itself is marked "soon". Replace the element when the screen lands.
  { path: APP_ROUTES.purchaseOrderCheck, element: <UnderDevelopment /> },
  { path: APP_ROUTES.paymentVoucherCompleted, element: withSuspense(<PaymentCompletedPage />) },
  { path: APP_ROUTES.pettyCash, element: withSuspense(<PettyCashFormPage />) },
  { path: APP_ROUTES.pettyCashView, element: withSuspense(<PettyCashViewPage />) },
  { path: "/ExpenseEntry", element: <UnderDevelopment /> },
  { path: "/RenewalEntry", element: <UnderDevelopment /> },
  { path: "/SalaryEntry", element: <UnderDevelopment /> },
  { path: APP_ROUTES.fuelEntry, element: withSuspense(<FuelEntryListPage />) },
  { path: APP_ROUTES.fuelEntryNew, element: withSuspense(<FuelEntryFormPage />) },
  { path: APP_ROUTES.fuelEntryEdit(), element: withSuspense(<FuelEntryFormPage />) },
  // Incident reports. `new` is declared before the `:id` route so the literal
  // wins the match instead of being read as an id.
  { path: APP_ROUTES.irReport, element: withSuspense(<IrListPage />) },
  { path: APP_ROUTES.irReportNew, element: withSuspense(<IrFormPage />) },
  { path: APP_ROUTES.irReportEdit(), element: withSuspense(<IrFormPage />) },
  { path: APP_ROUTES.gpsEngineHours, element: withSuspense(<EngineHoursPage />) },
  { path: APP_ROUTES.gpsSpeedReport, element: withSuspense(<SpeedReportPage />) },
  { path: APP_ROUTES.gpsFuelFillings, element: withSuspense(<FuelFillingsPage />) },
  { path: APP_ROUTES.tollEntry, element: withSuspense(<TollEntryListPage />) },
  { path: APP_ROUTES.tollEntryNew, element: withSuspense(<TollEntryFormPage />) },
  { path: APP_ROUTES.tollEntryEdit(), element: withSuspense(<TollEntryFormPage />) },
  { path: APP_ROUTES.leviEntry, element: withSuspense(<LeviEntryListPage />) },
  { path: APP_ROUTES.leviEntryNew, element: withSuspense(<LeviEntryFormPage />) },
  { path: APP_ROUTES.leviEntryEdit(), element: withSuspense(<LeviEntryFormPage />) },
  { path: APP_ROUTES.autoPassEntry, element: withSuspense(<AutoPassEntryListPage />) },
  { path: APP_ROUTES.autoPassEntryNew, element: withSuspense(<AutoPassEntryFormPage />) },
  { path: APP_ROUTES.autoPassEntryEdit(), element: withSuspense(<AutoPassEntryFormPage />) },
  { path: APP_ROUTES.paymentVoucher, element: withSuspense(<PaymentVoucherFormPage />) },
  { path: "/PaymentVoucher/AddPaymentVoucher", element: withSuspense(<PaymentVoucherFormPage />) },
  { path: APP_ROUTES.paymentVoucherView, element: withSuspense(<PaymentVoucherViewPage />) },
  { path: APP_ROUTES.paymentVoucherEdit(), element: withSuspense(<PaymentVoucherFormPage />) },
];

export const utilityRoutes: RouteObject[] = utilityRouteConfigs.map(({ path, element }) =>
  createProtectedIndexRoute(path, element)
);

export const payableRoutes: RouteObject[] = payableRouteConfigs.map(({ path, element }) =>
  createProtectedIndexRoute(path, element)
);
